/* Launch cart enhancement. Shopify's cart remains the source of truth. */
(() => {
  'use strict';

  const MARKER = 'lancio-ottobre-2026';
  const OPTOUT_KEY = 'bp-launch-gift-optout-20261014';
  const DEFAULT_THRESHOLD = 6125;
  let launch;
  let optOutMemory = false;
  let pending = Promise.resolve();
  let scheduled = false;
  let lastCart = null;
  let lastPhase = '';
  let lastContextValid = false;
  let lastDiscountCode = '';
  let discountFeedback = null;
  let pendingCount = 0;
  let checkoutLockActive = false;

  const root = () => window.Shopify?.routes?.root || '/';
  const endpoint = (path) => `${root()}${path === 'cart.js' ? path : `cart/${path}`}`;
  const giftId = () => String(launch?.config?.giftVariantId || '');
  const threshold = () => Number(launch?.config?.thresholdCents) || DEFAULT_THRESHOLD;
  const state = () => launch?.state?.() || { active: false, ended: false, before: true };
  const phase = () => state().active ? 'active' : state().ended ? 'ended' : 'before';
  const contextValid = () => state().valid !== false && /^\d+$/.test(giftId());
  const offerCode = () => String(launch?.config?.code || 'FUORITUTTO');
  const isGift = (item) => String(item.variant_id || item.id) === giftId();
  const isPromo = (item) => isGift(item) && item.properties?._omaggio === MARKER;
  const isOtherGift = (item) => item.gift_card === true || String(item.product_type || '').trim().toLowerCase() === 'omaggio';
  const qualifyingSubtotal = (cart) => (cart.items || []).reduce((sum, item) => {
    if (isGift(item) || isOtherGift(item)) return sum;
    return sum + (Number(item.original_line_price) || 0);
  }, 0);
  const money = (cents, currency) => new Intl.NumberFormat('it-IT', {
    style: 'currency', currency: currency || 'EUR'
  }).format(cents / 100);
  const discountAmount = (cart, code) => {
    const matching = (title) => title?.toLowerCase() === code.toLowerCase();
    const cartAmount = (cart.cart_level_discount_applications || [])
      .filter((app) => matching(app.title))
      .reduce((sum, app) => sum + (Number(app.total_allocated_amount) || 0), 0);
    const lineAmount = (cart.items || []).flatMap((item) => item.line_level_discount_allocations || [])
      .filter((allocation) => matching(allocation.discount_application?.title))
      .reduce((sum, allocation) => sum + (Number(allocation.amount) || 0), 0);
    return cartAmount + lineAmount;
  };
  const optOut = () => {
    try { return sessionStorage.getItem(OPTOUT_KEY) === '1'; }
    catch { return optOutMemory; }
  };
  const setOptOut = (value) => {
    optOutMemory = value;
    try { value ? sessionStorage.setItem(OPTOUT_KEY, '1') : sessionStorage.removeItem(OPTOUT_KEY); }
    catch { /* Private browsing can deny storage. In-memory intent still applies. */ }
  };

  function lockCheckout() {
    document.querySelectorAll('button[name="checkout"], input[name="checkout"]').forEach((button) => {
      if (button.disabled || button.hasAttribute('data-bp-launch-checkout-lock')) return;
      button.disabled = true;
      button.setAttribute('data-bp-launch-checkout-lock', '');
    });
  }

  function unlockCheckout() {
    document.querySelectorAll('[data-bp-launch-checkout-lock]').forEach((button) => {
      button.removeAttribute('data-bp-launch-checkout-lock');
      button.disabled = false;
    });
  }

  function queueWork(work) {
    pendingCount += 1;
    if (phase() !== 'before' && contextValid()) { checkoutLockActive = true; lockCheckout(); }
    pending = pending.then(work).catch((error) => {
      console.error('[bp-launch-cart]', error);
      if (lastCart) render(lastCart);
    }).finally(() => {
      pendingCount -= 1;
      if (pendingCount === 0) { checkoutLockActive = false; unlockCheckout(); }
    });
  }

  async function request(path, body) {
    const response = await fetch(endpoint(path), body ? {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    } : { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Cart ${path}: ${response.status}`);
    const data = await response.json();
    if (data?.errors) throw new Error(String(data.description || data.errors));
    return data;
  }

  async function refreshView() {
    document.dispatchEvent(new CustomEvent('cart-drawer:refresh'));
    if (document.querySelector('#main-cart-items')) {
      const footer = document.querySelector('#main-cart-footer');
      if (footer?.dataset.id) {
        try {
          const response = await fetch(`${root()}?section_id=${encodeURIComponent(footer.dataset.id)}`);
          if (response.ok) {
            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            const incoming = html.querySelector('#main-cart-footer .js-contents');
            const existing = footer.querySelector('.js-contents');
            if (incoming && existing) existing.innerHTML = incoming.innerHTML;
          }
        } catch (error) { console.error('[bp-launch-cart] footer refresh', error); }
      }
    }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bp-launch-cart' });
    }
  }

  function render(cart) {
    const current = state();
    const subtotal = qualifyingSubtotal(cart);
    const gift = (cart.items || []).find(isGift);
    const hasFreeGift = Boolean(gift && gift.final_line_price === 0);
    document.querySelectorAll('[data-bp-launch-cart]').forEach((container) => {
      container.hidden = !current.active || current.valid === false || cart.currency !== 'EUR' || !/^\d+$/.test(giftId());
      if (container.hidden) return;
      const message = container.querySelector('[data-bp-launch-gift]');
      const progress = container.querySelector('[data-bp-launch-progress]');
      const restore = container.querySelector('[data-bp-launch-restore]');
      const track = container.querySelector('[data-bp-launch-track]');
      const fill = container.querySelector('[data-bp-launch-fill]');
      restore.hidden = !optOut() || subtotal < threshold();
      progress.hidden = subtotal >= threshold();
      const reached = Math.min(subtotal, threshold());
      track.setAttribute('aria-valuemax', String(threshold()));
      track.setAttribute('aria-valuenow', String(reached));
      fill.style.width = `${Math.round(reached / threshold() * 100)}%`;
      if (optOut()) {
        message.textContent = 'Hai rimosso l’organizer omaggio. Puoi aggiungerlo di nuovo.';
      } else if (hasFreeGift) {
        message.textContent = 'Organizer con specchio sbloccato: è nel carrello, gratis.';
      } else if (subtotal < threshold()) {
        const percent = Number(launch.config.percent) || 20;
        const missing = Math.round((threshold() - subtotal) * (1 - percent / 100));
        message.textContent = `Con il codice ${offerCode()} ti mancano ${money(missing, cart.currency)} per l’organizer con specchio e la spedizione gratis`;
      } else if (gift) {
        message.textContent = `Organizer nel carrello a ${money(gift.final_line_price, cart.currency)}: l’omaggio gratuito non risulta applicato. Verifica il totale prima del pagamento.`;
      } else {
        message.textContent = 'Hai raggiunto la soglia omaggio. Verifica il prezzo dell’organizer nel carrello.';
      }
    });
    document.querySelectorAll('[data-bp-launch-discount-form]').forEach((form) => {
      const result = form.querySelector('[data-bp-launch-discount-result]');
      const input = form.querySelector('[data-bp-launch-discount-input]');
      if (input && lastDiscountCode && document.activeElement !== input) input.value = lastDiscountCode;
      if (result) {
        const code = discountFeedback?.code || lastDiscountCode || input?.value.trim() || offerCode();
        const amount = discountAmount(cart, code);
        if (discountFeedback?.kind === 'error') result.textContent = discountFeedback.message;
        else if (amount > 0) result.textContent = `Sconto ${code} applicato: −${money(amount, cart.currency)}.`;
        else result.textContent = discountFeedback?.kind === 'success' ? 'Lo sconto non risulta più applicato al carrello.' : '';
      }
    });
  }

  async function reconcile() {
    const current = state();
    if (!contextValid()) {
      document.querySelectorAll('[data-bp-launch-cart]').forEach((container) => { container.hidden = true; });
      return;
    }
    if (current.before && !current.active) return;
    let cart = await request('cart.js');
    if (cart.currency !== 'EUR') {
      document.querySelectorAll('[data-bp-launch-cart]').forEach((container) => { container.hidden = true; });
      return;
    }
    let changed = false;
    const gifts = (cart.items || []).filter(isGift);
    const qualified = current.active && qualifyingSubtotal(cart) >= threshold() && !optOut();
    if (!qualified) {
      while ((cart.items || []).some(isGift)) {
        const item = cart.items.find(isGift);
        const before = cart.items.filter(isGift).length;
        cart = await request('change.js', { id: item.key, quantity: 0 });
        if ((cart.items || []).filter(isGift).length >= before) throw new Error('Organizer removal was not confirmed');
        changed = true;
      }
    } else if (gifts.length) {
      while ((cart.items || []).filter(isGift).length > 1) {
        const item = cart.items.filter(isGift)[1];
        const before = cart.items.filter(isGift).length;
        cart = await request('change.js', { id: item.key, quantity: 0 });
        if ((cart.items || []).filter(isGift).length >= before) throw new Error('Duplicate organizer removal was not confirmed');
        changed = true;
      }
      const first = (cart.items || []).find(isGift);
      if (first?.quantity > 1) {
        cart = await request('change.js', { id: first.key, quantity: 1 });
        if ((cart.items || []).find(isGift)?.quantity !== 1) throw new Error('Organizer quantity lock was not confirmed');
        changed = true;
      }
    } else {
      try {
        await request('add.js', { items: [{ id: Number(giftId()), quantity: 1, properties: { _omaggio: MARKER } }] });
        cart = await request('cart.js');
        changed = true;
      } catch (error) {
        console.error('[bp-launch-cart] gift add', error);
      }
    }
    lastCart = cart;
    render(cart);
    document.dispatchEvent(new CustomEvent('bp:launch:cart', { detail: { cart } }));
    if (changed) await refreshView();
    return cart;
  }

  const campaignActive = () => state().active && contextValid();

  // PDP carts must await the same serialized gift write before opening or checking out.
  function sync() {
    const work = pending.then(async () => {
      await Promise.resolve(launch?.ready);
      if (!launch || !contextValid()) throw new Error('Launch cart context unavailable');
      pendingCount += 1;
      checkoutLockActive = true;
      lockCheckout();
      try { return await reconcile(); }
      finally {
        pendingCount -= 1;
        if (pendingCount === 0) { checkoutLockActive = false; unlockCheckout(); }
      }
    });
    pending = work.catch((error) => { console.error('[bp-launch-cart] sync', error); });
    return work;
  }

  async function openNative(opener) {
    if (!campaignActive()) return false;
    const cart = await sync();
    if (!campaignActive() || cart?.currency !== 'EUR') return false;
    const drawer = document.querySelector('cart-drawer');
    const target = document.querySelector('#CartDrawer');
    if (!drawer || !target || typeof drawer.toggleDrawerClasses !== 'function') throw new Error('Native cart drawer unavailable');
    const url = new URL(root(), location.origin);
    const previewThemeId = new URLSearchParams(location.search).get('preview_theme_id');
    if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);
    url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
    const response = await fetch(url.toString(), { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error(`Native cart sections: ${response.status}`);
    const sections = await response.json();
    if (!campaignActive()) { await sync(); return false; }
    const parser = new DOMParser();
    const incoming = parser.parseFromString(sections['cart-drawer'] || '', 'text/html').querySelector('#CartDrawer');
    if (!incoming) throw new Error('Native cart section missing');
    target.innerHTML = incoming.innerHTML;
    const bubble = document.querySelector('#cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      const source = parser.parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
      if (source) bubble.innerHTML = source.innerHTML;
    }
    render(cart);
    if (!drawer.isOpen) {
      drawer.setActiveElement?.(opener);
      drawer.toggleDrawerClasses();
    }
    return { cart, sections };
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    pendingCount += 1;
    if (phase() !== 'before' && contextValid()) { checkoutLockActive = true; lockCheckout(); }
    setTimeout(() => {
      scheduled = false;
      pending = pending.then(reconcile).catch((error) => {
        console.error('[bp-launch-cart]', error);
        if (lastCart) render(lastCart);
      }).finally(() => {
        pendingCount -= 1;
        if (pendingCount === 0) { checkoutLockActive = false; unlockCheckout(); }
      });
    }, 100);
  }

  async function applyDiscount(form) {
    const result = form.querySelector('[data-bp-launch-discount-result]');
    const button = form.querySelector('[data-bp-launch-discount-button]');
    const code = form.querySelector('[data-bp-launch-discount-input]')?.value.trim() || '';
    lastDiscountCode = code;
    discountFeedback = null;
    const say = (message, kind = 'error') => {
      discountFeedback = { code, kind, message };
      result.textContent = message;
    };
    const generic = form.hasAttribute('data-bp-cart-discount');
    if (!generic && (!state().active || !contextValid())) { say('La promozione non è attiva.'); return; }
    if (!code) { say('Inserisci un codice sconto.'); return; }
    button.disabled = true;
    try {
      const before = await request('cart.js');
      if (!generic && before.currency !== 'EUR') { say('Il codice non è disponibile per questa valuta.'); return; }
      await request('update.js', { discount: code });
      const after = await request('cart.js');
      lastCart = after;
      const amount = discountAmount(after, code);
      say(amount > 0 ? `Sconto ${code} applicato: −${money(amount, after.currency)}.` : 'Codice non valido o non applicabile a questo carrello.', amount > 0 ? 'success' : 'error');
      render(after);
      await refreshView();
      schedule();
    } catch (error) {
      console.error('[bp-launch-cart] discount', error);
      say(`Non siamo riusciti a verificare il codice. Puoi inserire ${code || offerCode()} al pagamento.`);
    } finally { button.disabled = false; }
  }

  function init() {
    launch = window.BPLaunch;
    if (!launch) return;
    Promise.resolve(launch.ready).then(() => {
      lastPhase = phase();
      lastContextValid = contextValid();
      if (lastPhase !== 'before') schedule();
      const onClock = () => {
        const next = phase();
        const nextValid = contextValid();
        if (next !== lastPhase || nextValid !== lastContextValid) {
          lastPhase = next;
          lastContextValid = nextValid;
          schedule();
        }
      };
      document.addEventListener('bp:launch:tick', onClock);
      document.addEventListener('visibilitychange', () => { if (!document.hidden) { onClock(); if (lastCart && phase() === 'active') schedule(); } });
      window.addEventListener('pageshow', () => { onClock(); if (lastCart && phase() === 'active') schedule(); });
      if (typeof PUB_SUB_EVENTS !== 'undefined') {
        document.addEventListener(PUB_SUB_EVENTS.cartDrawerOpen, schedule);
      }
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source !== 'bp-launch-cart') schedule();
        });
      }
    });
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-bp-launch-gift-line] cart-remove-button')) {
      setOptOut(true);
    }
    if (event.target.closest('[data-bp-launch-restore]')) {
      setOptOut(false);
      schedule();
    }
  }, true);
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-bp-launch-discount-form]');
    if (!form) return;
    event.preventDefault();
    if (form.dataset.bpSubmitting === 'true') return;
    form.dataset.bpSubmitting = 'true';
    queueWork(async () => { try { await applyDiscount(form); } finally { delete form.dataset.bpSubmitting; } });
  });
  document.addEventListener('shopify:section:load', schedule);
  new MutationObserver((records) => {
    if (checkoutLockActive) lockCheckout();
    if (!lastCart) return;
    if (records.some((record) => Array.from(record.addedNodes).some((node) =>
      node.nodeType === 1 && (node.matches?.('[data-bp-launch-cart], [data-bp-launch-discount-form]') || node.querySelector?.('[data-bp-launch-cart], [data-bp-launch-discount-form]'))))) {
      render(lastCart);
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
  window.BPLaunchCart = { schedule, sync, campaignActive, openNative, qualifyingSubtotal, isPromo, isGift };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
